import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { InputTextarea } from "primereact/inputtextarea";
import { Tag } from "primereact/tag";
import { Toast } from 'primereact/toast';
import { ToggleButton } from "primereact/togglebutton";
import { classNames } from "primereact/utils";
import React, { useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CarePlanSectionProps, NA, NO, RESIDENT_CARE_PLAN_TYPE, RESIDENT_CARE_PLAN_TYPE_MESSAGES, YES } from "../../../constants/constants";
import {
  ASSISTANCE_REQUIRED_MESSAGE, SUGGESTED_INSTRUCTIONS_ASSAULTIVE_NOT_AT_PERSONAL_CARE, SUGGESTED_INSTRUCTIONS_BREAK_THROWS_ITEMS, SUGGESTED_INSTRUCTIONS_CRYTING, SUGGESTED_INSTRUCTIONS_DELUSIONS,
  SUGGESTED_INSTRUCTIONS_FEAR_SUSPICIOUS, SUGGESTED_INSTRUCTIONS_HALLUCINATIONS, SUGGESTED_INSTRUCTIONS_IRRITABLE,
  SUGGESTED_INSTRUCTIONS_NIGHT_DISRUPTIONS, SUGGESTED_INSTRUCTIONS_NON_HEALTH_REPITITIVE_ANXIETY,
  SUGGESTED_INSTRUCTIONS_RESISTIVE_TO_CARE_WITH_WORD, SUGGESTED_INSTRUCTIONS_SELF_INJURY, SUGGESTED_INSTRUCTIONS_SPITTING, SUGGESTED_INSTRUCTIONS_VERBAL_NOISES,
  SUGGESTED_INSTRUCTIONS_WANDERS_EXIT_SEEKING,
  SUGGESTED_INSTRUCTIONS_WANDERS_NOT_EXIT_SEEKING
} from "../../../constants/carePlanConstants";
import SuggestedInstructionsDialog from "./suggestedInstructionsDialog";
import { set } from "mobx";
import { DropDownOption } from "../../../models/primereact";
import SectionGenieDialog from "./SectionGenieDialog";
import {
  PSYCH_SOCIAL_GENIE_FIELD_DEFINITIONS,
  PSYCH_SOCIAL_GENIE_QUESTIONS,
  PsychSocialGenieSectionValues,
} from "./psychSocialGenie";
import { useCarePlanAutoSave } from "./useCarePlanAutoSave";
import { PSYCH_SOCIAL_BEHAVIOR_REGISTRY, PsychSocialBehaviorFieldConfig } from "./psychSocialBehaviorRegistry";

const PsychSocialSection: React.FC<CarePlanSectionProps> = ({
  sectionData, submitButtonClick, backButtonClick, nextButtonClick, showHelpMessage, loadingCarePlan
}) => {
  const [enableEditing, setEnableEditing] = useState<boolean>(false);
  const [showGenieDialog, setShowGenieDialog] = useState<boolean>(false);
  const toast = useRef<Toast>(null);
  const [showSuggestedInstructionsDialog, setShowSuggestedInstructionsDialog] = useState<boolean>(false);
  const [suggestedInstructions, setSuggestedInstructions] = useState<any[]>([]);
  const [instructionsFieldName, setInstructionsFieldName] = useState<string>("");
  const [selectedBehaviorGuidance, setSelectedBehaviorGuidance] = useState<PsychSocialBehaviorFieldConfig | null>(null);
  const dynamicBehaviorDefaults = PSYCH_SOCIAL_BEHAVIOR_REGISTRY.reduce((acc, behavior) => {
    const detailsFieldName = `${behavior.fieldPrefix}Details`;
    const assistanceFieldName = `${behavior.fieldPrefix}AssistanceDetails`;
    acc[`any${behavior.fieldPrefix.charAt(0).toUpperCase()}${behavior.fieldPrefix.slice(1)}`] =
      sectionData?.[`any${behavior.fieldPrefix.charAt(0).toUpperCase()}${behavior.fieldPrefix.slice(1)}`] || false;
    acc[detailsFieldName] = sectionData?.[detailsFieldName] || '';
    acc[assistanceFieldName] = sectionData?.[assistanceFieldName] || '';
    return acc;
  }, {} as Record<string, any>);

  const defaultValues = {
    carePlanType: RESIDENT_CARE_PLAN_TYPE.PSYCH_SOCIAL_COGNITIVE,

    anySleepDisturbances: sectionData?.anySleepDisturbances || false,
    sleepDisturbancesDetails: sectionData?.sleepDisturbancesDetails || '',
    sleepDisturbancesAssistanceDetails: sectionData?.sleepDisturbancesAssistanceDetails || '',

    anyNightTimeAssistance: sectionData?.anyNightTimeAssistance || false,
    nightTimeAssistanceDetails: sectionData?.nightTimeAssistanceDetails || '',
    nightTimeAssistanceAssistanceDetails: sectionData?.nightTimeAssistanceAssistanceDetails || '',

    anyShortTermMemoryIssues: sectionData?.anyShortTermMemoryIssues || false,
    shortTermMemoryIssuesDetails: sectionData?.shortTermMemoryIssuesDetails || '',
    shortTermMemoryIssuesAssistanceDetails: sectionData?.shortTermMemoryIssuesAssistanceDetails || '',

    anyLongTermMemoryIssues: sectionData?.anyLongTermMemoryIssues || false,
    longTermMemoryIssuesDetails: sectionData?.longTermMemoryIssuesDetails || '',
    longTermMemoryIssuesAssistanceDetails: sectionData?.longTermMemoryIssuesAssistanceDetails || '',

    anyOrientedToPerson: sectionData?.anyOrientedToPerson || false,
    orientedToPersonDetails: sectionData?.orientedToPersonDetails || '',
    orientedToPersonAssistanceDetails: sectionData?.orientedToPersonAssistanceDetails || '',

    anyImpairedDecisionMaking: sectionData?.anyImpairedDecisionMaking || false,
    impairedDecisionMakingDetails: sectionData?.impairedDecisionMakingDetails || '',
    impairedDecisionMakingAssistanceDetails: sectionData?.impairedDecisionMakingAssistanceDetails || '',

    anyDisruptiveBehavior: sectionData?.anyDisruptiveBehavior || false,
    disruptiveBehaviorDetails: sectionData?.disruptiveBehaviorDetails || '',
    disruptiveBehaviorAssistanceDetails: sectionData?.disruptiveBehaviorAssistanceDetails || '',

    anyAssaultiveBehavior: sectionData?.anyAssaultiveBehavior || false,
    assaultiveBehaviorDetails: sectionData?.assaultiveBehaviorDetails || '',
    assaultiveBehaviorAssistanceDetails: sectionData?.assaultiveBehaviorAssistanceDetails || '',

    anyResistanceToCare: sectionData?.anyResistanceToCare || false,
    resistanceToCareDetails: sectionData?.resistanceToCareDetails || '',
    resistanceToCareAssistanceDetails: sectionData?.resistanceToCareAssistanceDetails || '',

    anyDepression: sectionData?.anyDepression || false,
    depressionDetails: sectionData?.depressionDetails || '',
    depressionAssistanceDetails: sectionData?.depressionAssistanceDetails || '',

    anyAnxiety: sectionData?.anyAnxiety || false,
    anxietyDetails: sectionData?.anxietyDetails || '',
    anxietyAssistanceDetails: sectionData?.anxietyAssistanceDetails || '',

    anyIrritability: sectionData?.anyIrritability || false,
    irritabilityDetails: sectionData?.irritabilityDetails || '',
    irritabilityAssistanceDetails: sectionData?.irritabilityAssistanceDetails || '',

    anyDisorientation: sectionData?.anyDisorientation || false,
    disorientationDetails: sectionData?.disorientationDetails || '',
    disorientationAssistanceDetails: sectionData?.disorientationAssistanceDetails || '',

    anyWandering: sectionData?.anyWandering || false,
    wanderingDetails: sectionData?.wanderingDetails || '',
    wanderingAssistanceDetails: sectionData?.wanderingAssistanceDetails || '',

    anyExitSeekingBehavior: sectionData?.anyExitSeekingBehavior || false,
    exitSeekingBehaviorDetails: sectionData?.exitSeekingBehaviorDetails || '',
    exitSeekingBehaviorAssistanceDetails: sectionData?.exitSeekingBehaviorAssistanceDetails || '',

    anyHallucinations: sectionData?.anyHallucinations || false,
    hallucinationsDetails: sectionData?.hallucinationsDetails || '',
    hallucinationsAssistanceDetails: sectionData?.hallucinationsAssistanceDetails || '',

    anyDelusions: sectionData?.anyDelusions || false,
    delusionsDetails: sectionData?.delusionsDetails || '',
    delusionsAssistanceDetails: sectionData?.delusionsAssistanceDetails || '',

    anyVerbalAggression: sectionData?.anyVerbalAggression || false,
    verbalAggressionDetails: sectionData?.verbalAggressionDetails || '',
    verbalAggressionAssistanceDetails: sectionData?.verbalAggressionAssistanceDetails || '',

    anyPhysicalAggression: sectionData?.anyPhysicalAggression || false,
    physicalAggressionDetails: sectionData?.physicalAggressionDetails || '',
    physicalAggressionAssistanceDetails: sectionData?.physicalAggressionAssistanceDetails || '',

    anyInappropriateBehavior: sectionData?.anyInappropriateBehavior || false,
    inappropriateBehaviorDetails: sectionData?.inappropriateBehaviorDetails || '',
    inappropriateBehaviorAssistanceDetails: sectionData?.inappropriateBehaviorAssistanceDetails || '',

    anySuicidalBehavior: sectionData?.anySuicidalBehavior || false,
    suicidalBehaviorDetails: sectionData?.suicidalBehaviorDetails || '',
    suicidalBehaviorAssistanceDetails: sectionData?.suicidalBehaviorAssistanceDetails || '',

    anyDifficultyInNewSituations: sectionData?.anyDifficultyInNewSituations || false,
    difficultyInNewSituationsDetails: sectionData?.difficultyInNewSituationsDetails || '',
    difficultyInNewSituationsAssistanceDetails: sectionData?.difficultyInNewSituationsAssistanceDetails || '',

    anyDisrobing: sectionData?.anyDisrobing || false,
    disrobingDetails: sectionData?.disrobingDetails || '',
    disrobingAssistanceDetails: sectionData?.disrobingAssistanceDetails || '',

    anyWeeping: sectionData?.anyWeeping || false,
    weepingDetails: sectionData?.weepingDetails || '',
    weepingAssistanceDetails: sectionData?.weepingAssistanceDetails || '',

    anyUnawareOfConsequences: sectionData?.anyUnawareOfConsequences || false,
    unawareOfConsequencesDetails: sectionData?.unawareOfConsequencesDetails || '',
    unawareOfConsequencesAssistanceDetails: sectionData?.unawareOfConsequencesAssistanceDetails || '',

    anyUnrealisticFears: sectionData?.anyUnrealisticFears || false,
    unrealisticFearsDetails: sectionData?.unrealisticFearsDetails || '',
    unrealisticFearsAssistanceDetails: sectionData?.unrealisticFearsAssistanceDetails || '',

    anyInappropriateSpitting: sectionData?.anyInappropriateSpitting || false,
    inappropriateSpittingDetails: sectionData?.inappropriateSpittingDetails || '',
    inappropriateSpittingAssistanceDetails: sectionData?.inappropriateSpittingAssistanceDetails || '',

    anyBreakThings: sectionData?.anyBreakThings || false,
    breakThingsDetails: sectionData?.breakThingsDetails || '',
    breakThingsAssistanceDetails: sectionData?.breakThingsAssistanceDetails || '',

    anyOtherPsychSocialCognitiveIssues: sectionData?.anyOtherPsychSocialCognitiveIssues || false,
    otherPsychSocialCognitiveIssuesDetails: sectionData?.otherPsychSocialCognitiveIssuesDetails || '',
    otherPsychSocialCognitiveIssuesAssistanceDetails: sectionData?.otherPsychSocialCognitiveIssuesAssistanceDetails || '',

    anyPsychopharmacologicMedications: sectionData?.anyPsychopharmacologicMedications || false,
    psychopharmacologicMedicationsDetails: sectionData?.psychopharmacologicMedicationsDetails || '',
    psychopharmacologicMedicationsAssistanceDetails: sectionData?.psychopharmacologicMedicationsAssistanceDetails || '',

    anyBehavioralHealthSupportCrisisPlan: sectionData?.anyBehavioralHealthSupportCrisisPlan || false,
    behavioralHealthSupportCrisisPlanDetails: sectionData?.behavioralHealthSupportCrisisPlanDetails || '',
    behavioralHealthSupportCrisisPlanAssistanceDetails: sectionData?.behavioralHealthSupportCrisisPlanAssistanceDetails || '',

    anyCounselingFromMentalHealthProfessional: sectionData?.anyCounselingFromMentalHealthProfessional || false,
    counselingFromMentalHealthProfessionalDetails: sectionData?.counselingFromMentalHealthProfessionalDetails || '',
    counselingFromMentalHealthProfessionalAssistanceDetails: sectionData?.counselingFromMentalHealthProfessionalAssistanceDetails || '',

    anyPastBehaviors: sectionData?.anyPastBehaviors || false,
    pastBehaviorsDetails: sectionData?.pastBehaviorsDetails || '',
    pastBehaviorsAssistanceDetails: sectionData?.pastBehaviorsAssistanceDetails || '',

    anyMeaningfulDayProgram: sectionData?.anyMeaningfulDayProgram || false,
    meaningfulDayProgramDetails: sectionData?.meaningfulDayProgramDetails || '',
    meaningfulDayProgramAssistanceDetails: sectionData?.meaningfulDayProgramAssistanceDetails || '',

    anyExpandedCommunityServices: sectionData?.anyExpandedCommunityServices || false,
    expandedCommunityServicesDetails: sectionData?.expandedCommunityServicesDetails || '',
    expandedCommunityServicesAssistanceDetails: sectionData?.expandedCommunityServicesAssistanceDetails || '',

    anySpecializedBehavioralPrograms: sectionData?.anySpecializedBehavioralPrograms || false,
    specializedBehavioralProgramsDetails: sectionData?.specializedBehavioralProgramsDetails || '',
    specializedBehavioralProgramsAssistanceDetails: sectionData?.specializedBehavioralProgramsAssistanceDetails || '',

    anyMentalHealthProgram: sectionData?.anyMentalHealthProgram || false,
    mentalHealthProgramDetails: sectionData?.mentalHealthProgramDetails || '',
    mentalHealthProgramAssistanceDetails: sectionData?.mentalHealthProgramAssistanceDetails || '',
    mentalHealthProgramContactInfo: sectionData?.mentalHealthProgramContactInfo || '',

    anyMeaningfulDayInterventions: sectionData?.anyMeaningfulDayInterventions || false,
    meaningfulDayInterventionsDetails: sectionData?.meaningfulDayInterventionsDetails || '',
    meaningfulDayInterventionsAssistanceDetails: sectionData?.meaningfulDayInterventionsAssistanceDetails || '',

    anyOtherInterventions: sectionData?.anyOtherInterventions || false,
    otherInterventionsDetails: sectionData?.otherInterventionsDetails || '',
    otherInterventionsAssistanceDetails: sectionData?.otherInterventionsAssistanceDetails || '',

    ...dynamicBehaviorDefaults,

    normalDayRoutineDetails: sectionData?.normalDayRoutineDetails || '',
  }

  const { control, formState: { errors }, handleSubmit, reset, watch, register, setValue, getValues } = useForm({ defaultValues });
  const watchAnySleepDisturbances = watch('anySleepDisturbances', defaultValues.anySleepDisturbances);
  const watchAnyNightTimeAssistance = watch('anyNightTimeAssistance', defaultValues.anyNightTimeAssistance);
  const watchAnyShortTermMemoryIssues = watch('anyShortTermMemoryIssues', defaultValues.anyShortTermMemoryIssues);
  const watchAnyLongTermMemoryIssues = watch('anyLongTermMemoryIssues', defaultValues.anyLongTermMemoryIssues);
  const watchAnyOrientedToPerson = watch('anyOrientedToPerson', defaultValues.anyOrientedToPerson);
  const watchAnyImpairedDecisionMaking = watch('anyImpairedDecisionMaking', defaultValues.anyImpairedDecisionMaking);
  const watchAnyDisruptiveBehavior = watch('anyDisruptiveBehavior', defaultValues.anyDisruptiveBehavior);
  const watchAnyAssaultiveBehavior = watch('anyAssaultiveBehavior', defaultValues.anyAssaultiveBehavior);
  const watchAnyResistanceToCare = watch('anyResistanceToCare', defaultValues.anyResistanceToCare);
  const watchAnyDepression = watch('anyDepression', defaultValues.anyDepression);
  const watchAnyAnxiety = watch('anyAnxiety', defaultValues.anyAnxiety);
  const watchAnyIrritability = watch('anyIrritability', defaultValues.anyIrritability);
  const watchAnyDisorientation = watch('anyDisorientation', defaultValues.anyDisorientation);
  const watchAnyWandering = watch('anyWandering', defaultValues.anyWandering);
  const watchAnyExitSeekingBehavior = watch('anyExitSeekingBehavior', defaultValues.anyExitSeekingBehavior);
  const watchAnyHallucinations = watch('anyHallucinations', defaultValues.anyHallucinations);
  const watchAnyDelusions = watch('anyDelusions', defaultValues.anyDelusions);
  const watchAnyVerbalAggression = watch('anyVerbalAggression', defaultValues.anyVerbalAggression);
  const watchAnyPhysicalAggression = watch('anyPhysicalAggression', defaultValues.anyPhysicalAggression);
  const watchAnyInappropriateBehavior = watch('anyInappropriateBehavior', defaultValues.anyInappropriateBehavior);
  const watchAnySuicidalBehavior = watch('anySuicidalBehavior', defaultValues.anySuicidalBehavior);
  const watchAnyDifficultyInNewSituations = watch('anyDifficultyInNewSituations', defaultValues.anyDifficultyInNewSituations);
  const watchAnyDisrobing = watch('anyDisrobing', defaultValues.anyDisrobing);
  const watchAnyWeeping = watch('anyWeeping', defaultValues.anyWeeping);
  const watchAnyUnawareOfConsequences = watch('anyUnawareOfConsequences', defaultValues.anyUnawareOfConsequences);
  const watchAnyUnrealisticFears = watch('anyUnrealisticFears', defaultValues.anyUnrealisticFears);
  const watchAnyInappropriateSpitting = watch('anyInappropriateSpitting', defaultValues.anyInappropriateSpitting);
  const watchAnyBreakThings = watch('anyBreakThings', defaultValues.anyBreakThings);
  const watchAnyOtherPsychSocialCognitiveIssues = watch('anyOtherPsychSocialCognitiveIssues', defaultValues.anyOtherPsychSocialCognitiveIssues);
  const watchAnyPsychopharmacologicMedications = watch('anyPsychopharmacologicMedications', defaultValues.anyPsychopharmacologicMedications);
  const watchAnyBehavioralHealthSupportCrisisPlan = watch('anyBehavioralHealthSupportCrisisPlan', defaultValues.anyBehavioralHealthSupportCrisisPlan);
  const watchAnyCounselingFromMentalHealthProfessional = watch('anyCounselingFromMentalHealthProfessional', defaultValues.anyCounselingFromMentalHealthProfessional);
  const watchAnyPastBehaviors = watch('anyPastBehaviors', defaultValues.anyPastBehaviors);
  const watchAnyMeaningfulDayProgram = watch('anyMeaningfulDayProgram', defaultValues.anyMeaningfulDayProgram);
  const watchAnyExpandedCommunityServices = watch('anyExpandedCommunityServices', defaultValues.anyExpandedCommunityServices);
  const watchAnySpecializedBehavioralPrograms = watch('anySpecializedBehavioralPrograms', defaultValues.anySpecializedBehavioralPrograms);
  const watchAnyMentalHealthProgram = watch('anyMentalHealthProgram', defaultValues.anyMentalHealthProgram);
  const watchAnyMeaningfulDayInterventions = watch('anyMeaningfulDayInterventions', defaultValues.anyMeaningfulDayInterventions);
  const watchAnyOtherInterventions = watch('anyOtherInterventions', defaultValues.anyOtherInterventions);

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const onsubmit = async (data: any) => {
    submitButtonClick && submitButtonClick(data);
  }

  const onManualSave = async (data: any) => {
    submitButtonClick && submitButtonClick(data, { showSuccessToast: true });
  }

  useCarePlanAutoSave({
    enabled: enableEditing,
    baselineData: defaultValues,
    watch,
    handleSubmit,
    onSubmit: onsubmit,
  });

  const actions = () => {
    return (
      <div className="flex justify-content-center gap-2">
        <Button label="Prev" icon="pi pi-arrow-left" severity="info" outlined rounded iconPos="left" onClick={backButtonClick} />
        <Button
          label="Use Genie's Help to Fill"
          icon="pi pi-sparkles"
          severity="warning"
          rounded
          onClick={() => {
            setEnableEditing(true);
            setShowGenieDialog(true);
          }}
          visible={enableEditing}
        />
        <Button label="Edit" icon="pi pi-pencil" severity="warning" outlined rounded onClick={() => setEnableEditing(true)} visible={!enableEditing} />
        <Button label="Disable Edit" icon="pi pi-lock" visible={enableEditing} severity="secondary" outlined rounded onClick={() => { setEnableEditing(false); reset(defaultValues); }} />
        <Button label="Save" loading={loadingCarePlan} disabled={!enableEditing} severity="warning" outlined rounded onClick={handleSubmit(onManualSave)} visible={enableEditing} />
        <Button label="Next" icon="pi pi-arrow-right" severity="info" outlined rounded iconPos="right"
          onClick={nextButtonClick} />
      </div>
    )
  };

  const renderSuggestedInstructionsDialog = (e, fieldName, instructions) => {
    e.preventDefault();
    setSuggestedInstructions(instructions);
    setInstructionsFieldName(fieldName);
    setShowSuggestedInstructionsDialog(true);
  }

  const onSaveSuggestedInstructions = (data) => {
    setShowSuggestedInstructionsDialog(false);
    let existingInstructions = getValues(data.fieldName) || "";
    if (data.instructions.trim() !== "") {
      setValue(data.fieldName, existingInstructions + "\n" + data.instructions, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    }
  };

  const formatBehaviorFieldName = (fieldPrefix: string) => {
    return `any${fieldPrefix.charAt(0).toUpperCase()}${fieldPrefix.slice(1)}`;
  };

  const renderBehaviorGuidance = (definition: string, potentialCauses: string[]) => (
    <div>
      <div style={{ whiteSpace: "pre-wrap", marginBottom: "0.75rem" }}>
        <strong>Definition: </strong>{definition}
      </div>
      <div>
        <strong>Potential Causes:</strong>
        <ul style={{ marginTop: "0.5rem", paddingLeft: "1.25rem" }}>
          {potentialCauses.map((cause, index) => (
            <li key={`${cause}-${index}`} style={{ marginBottom: "0.25rem" }}>{cause}</li>
          ))}
        </ul>
      </div>
    </div>
  );

  const renderRegistryBehaviorSections = () => {
    let lastCategory = "";
    return PSYCH_SOCIAL_BEHAVIOR_REGISTRY.map((behavior) => {
      const anyFieldName = formatBehaviorFieldName(behavior.fieldPrefix);
      const detailsFieldName = `${behavior.fieldPrefix}Details`;
      const assistanceFieldName = `${behavior.fieldPrefix}AssistanceDetails`;
      const isEnabled = watch(anyFieldName as any, defaultValues[anyFieldName]);
      const showCategoryDivider = lastCategory !== behavior.sectionCategory;
      lastCategory = behavior.sectionCategory;

      return (
        <React.Fragment key={behavior.fieldPrefix}>
          {showCategoryDivider && (
            <Divider>
              <Tag severity="info">{behavior.sectionCategory}</Tag>
            </Divider>
          )}
          {
            enableEditing ? (
              <div className="flex flex-wrap">
                <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                  <span className="flex align-items-center gap-2 mb-2">
                    <label className={classNames({ 'p-error': errors[anyFieldName as any] })}>Any {behavior.title}:</label>
                    <Button
                      type="button"
                      icon="pi pi-info-circle"
                      rounded
                      text
                      severity="info"
                      tooltip="View section guidance"
                      tooltipOptions={{ position: "top" }}
                      aria-label={`View guidance for ${behavior.title}`}
                      onClick={() => setSelectedBehaviorGuidance(behavior)}
                    />
                  </span>
                  <div className="inline-flex m-2 span-color-white">
                    <Controller
                      name={anyFieldName as any}
                      control={control}
                      render={({ field }) => (
                        <ToggleButton
                          onLabel="Yes"
                          offLabel="No"
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.value)}
                        />
                      )}
                    />
                  </div>
                  {getFormErrorMessage(anyFieldName)}
                </div>

                {
                  isEnabled && (
                    <>
                      <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                        <span className="flex align-items-center gap-2 mb-2">
                          <label htmlFor={detailsFieldName} className={classNames({ 'p-error': errors[detailsFieldName as any] })}>
                            Add {behavior.title} details:
                          </label>
                        </span>
                        <span className="p-float-label">
                          <Controller
                            name={detailsFieldName as any}
                            control={control}
                            rules={{ required: `${behavior.title} details is required.` }}
                            render={({ field, fieldState }) => (
                              <InputTextarea
                                placeholder={`Add ${behavior.title} details`}
                                style={{ width: '100%' }}
                                rows={5}
                                id={field.name}
                                {...field}
                                className={classNames({ 'p-invalid': fieldState.invalid })}
                              />
                            )}
                          />
                        </span>
                        {getFormErrorMessage(detailsFieldName)}
                      </div>

                      <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                        <span className="flex align-items-center gap-2 mb-2">
                          <label htmlFor={assistanceFieldName} className={classNames({ 'p-error': errors[assistanceFieldName as any] })}>
                            {ASSISTANCE_REQUIRED_MESSAGE}
                          </label>
                        </span>
                        <span className="p-float-label">
                          <Controller
                            name={assistanceFieldName as any}
                            control={control}
                            render={({ field, fieldState }) => (
                              <InputTextarea
                                placeholder={`Describe any assistance required for ${behavior.title.toLowerCase()}`}
                                style={{ width: '100%' }}
                                rows={5}
                                id={field.name}
                                {...field}
                                className={classNames({ 'p-invalid': fieldState.invalid })}
                              />
                            )}
                          />
                          <div className="flex justify-content-center">
                            <Button
                              icon="pi pi-info-circle"
                              label="Suggested instructions"
                              severity="warning"
                              rounded
                              outlined
                              onClick={(e) => { renderSuggestedInstructionsDialog(e, assistanceFieldName, behavior.suggestedInstructions); }}
                            />
                          </div>
                        </span>
                        {getFormErrorMessage(assistanceFieldName)}
                      </div>
                    </>
                  )
                }
              </div>
            ) : (
              <div className="flex flex-wrap justify-content-center align-items-center">
                <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                  <strong>Any {behavior.title}: </strong>{sectionData?.[anyFieldName] ? YES : NO}
                </div>
                {
                  sectionData?.[anyFieldName] && (
                    <>
                      <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                        <strong>{behavior.title} details </strong>
                        <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>
                          {(sectionData?.[detailsFieldName] || NA)}
                        </span>
                      </div>
                      <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}>
                        <strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                        <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>
                          {(sectionData?.[assistanceFieldName] || NA)}
                        </span>
                      </div>
                    </>
                  )
                }
              </div>
            )
          }
        </React.Fragment>
      );
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1em" }} className="m-2 p-component resident-careplan-section">
      <Toast ref={toast}></Toast>
      <SectionGenieDialog
        visible={showGenieDialog}
        title="Using Genie now"
        questions={PSYCH_SOCIAL_GENIE_QUESTIONS}
        fieldDefinitions={PSYCH_SOCIAL_GENIE_FIELD_DEFINITIONS}
        currentValues={getValues() as PsychSocialGenieSectionValues}
        successMessage="Psychosocial care plan updated."
        errorMessage="Unable to update the psychosocial care plan."
        onHide={() => setShowGenieDialog(false)}
        onApply={async (payload) => {
          const nextValues = { ...getValues(), ...payload.valueUpdates };
          Object.entries(payload.valueUpdates).forEach(([fieldKey, fieldValue]) => {
            setValue(fieldKey as keyof typeof defaultValues, fieldValue as never, { shouldDirty: true });
          });
          await onsubmit(nextValues);
        }}
      />

      <SuggestedInstructionsDialog showDialog={showSuggestedInstructionsDialog}
        fieldName={instructionsFieldName}
        submitButtonClick={(data) => { onSaveSuggestedInstructions(data); }}
        onClose={() => setShowSuggestedInstructionsDialog(false)}
        suggestedInstructionsData={suggestedInstructions} />

      <Dialog
        header={selectedBehaviorGuidance?.title || "Section guidance"}
        visible={!!selectedBehaviorGuidance}
        style={{ width: "50rem", maxWidth: "95vw" }}
        onHide={() => setSelectedBehaviorGuidance(null)}
      >
        {selectedBehaviorGuidance && renderBehaviorGuidance(
          selectedBehaviorGuidance.definition,
          selectedBehaviorGuidance.potentialCauses
        )}
      </Dialog>

      <Card className="resident-careplan-card" footer={actions()} header={actions()} >
        <div className="">
          <form className="">
            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Sleep</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anySleepDisturbances })}>Any Sleep Disturbances:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anySleepDisturbances"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anySleepDisturbances')}
                    </div>

                    {
                      watchAnySleepDisturbances && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="sleepDisturbancesDetails" className={classNames({ 'p-error': errors.sleepDisturbancesDetails })}>
                                Add Sleep Disturbances details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_SLEEP) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="sleepDisturbancesDetails" control={control}
                                rules={{ required: 'Sleep Disturbances details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Sleep Disturbances details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('sleepDisturbancesDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="sleepDisturbancesAssistanceDetails" className={classNames({ 'p-error': errors.sleepDisturbancesAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="sleepDisturbancesAssistanceDetails" control={control}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for sleep disturbances"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'sleepDisturbancesAssistanceDetails', SUGGESTED_INSTRUCTIONS_NIGHT_DISRUPTIONS); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('sleepDisturbancesAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Sleep Disturbances: </strong>{sectionData?.anySleepDisturbances ? YES : NO}</div>
                    {
                      sectionData?.anySleepDisturbances && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Sleep Disturbances details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.sleepDisturbancesDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.sleepDisturbancesAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Night time</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyNightTimeAssistance })}>Any Night Time Assistance:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyNightTimeAssistance"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyNightTimeAssistance')}
                    </div>

                    {
                      watchAnyNightTimeAssistance && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="nightTimeAssistanceDetails" className={classNames({ 'p-error': errors.nightTimeAssistanceDetails })}>
                                Add Night Time Assistance details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_NIGHTTIME) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="nightTimeAssistanceDetails" control={control}
                                rules={{ required: 'Night Time Assistance details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Night Time Assistance details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('nightTimeAssistanceDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="nightTimeAssistanceAssistanceDetails" className={classNames({ 'p-error': errors.nightTimeAssistanceAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="nightTimeAssistanceAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for night time assistance"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'nightTimeAssistanceAssistanceDetails', SUGGESTED_INSTRUCTIONS_NIGHT_DISRUPTIONS); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('nightTimeAssistanceAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Night Time Assistance: </strong>{sectionData?.anyNightTimeAssistance ? YES : NO}</div>
                    {
                      sectionData?.anyNightTimeAssistance && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Night Time Assistance details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.nightTimeAssistanceDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.nightTimeAssistanceAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Memory impaired - short term</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyShortTermMemoryIssues })}>Any Short-term Memory Issues:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyShortTermMemoryIssues"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyShortTermMemoryIssues')}
                    </div>

                    {
                      watchAnyShortTermMemoryIssues && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="shortTermMemoryIssuesDetails" className={classNames({ 'p-error': errors.shortTermMemoryIssuesDetails })}>
                                Add Short-term Memory Issues details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_SHORT_TERM) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="shortTermMemoryIssuesDetails" control={control}
                                rules={{ required: 'Short-term Memory Issues details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Short-term Memory Issues details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('shortTermMemoryIssuesDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="shortTermMemoryIssuesAssistanceDetails" className={classNames({ 'p-error': errors.shortTermMemoryIssuesAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="shortTermMemoryIssuesAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for short-term memory issues"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('shortTermMemoryIssuesAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Short-term Memory Issues: </strong>{sectionData?.anyShortTermMemoryIssues ? YES : NO}</div>
                    {
                      sectionData?.anyShortTermMemoryIssues && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Short-term Memory Issues details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.shortTermMemoryIssuesDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.shortTermMemoryIssuesAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Memory impaired - long term</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyLongTermMemoryIssues })}>Any Long-term Memory Issues:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyLongTermMemoryIssues"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyLongTermMemoryIssues')}
                    </div>

                    {
                      watchAnyLongTermMemoryIssues && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="longTermMemoryIssuesDetails" className={classNames({ 'p-error': errors.longTermMemoryIssuesDetails })}>
                                Add Long-term Memory Issues details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_LONG_TERM) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="longTermMemoryIssuesDetails" control={control}
                                rules={{ required: 'Long-term Memory Issues details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Long-term Memory Issues details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('longTermMemoryIssuesDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="longTermMemoryIssuesAssistanceDetails" className={classNames({ 'p-error': errors.longTermMemoryIssuesAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="longTermMemoryIssuesAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for long-term memory issues"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('longTermMemoryIssuesAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Sleep Disturbances: </strong>{sectionData?.anyLongTermMemoryIssues ? YES : NO}</div>
                    {
                      sectionData?.anyLongTermMemoryIssues && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Long-term Memory Issues details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.longTermMemoryIssuesDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.longTermMemoryIssuesAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Memory impaired - orientated to person</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyOrientedToPerson })}>Any oriented to person:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyOrientedToPerson"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyOrientedToPerson')}
                    </div>

                    {
                      watchAnyOrientedToPerson && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="orientedToPersonDetails" className={classNames({ 'p-error': errors.orientedToPersonDetails })}>
                                Add oriented to person details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="orientedToPersonDetails" control={control}
                                rules={{ required: 'Oriented to person details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add oriented to person details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('orientedToPersonDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="orientedToPersonAssistanceDetails" className={classNames({ 'p-error': errors.orientedToPersonAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="orientedToPersonAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for oriented to person"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('orientedToPersonAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Oriented To Person: </strong>{sectionData?.anyOrientedToPerson ? YES : NO}</div>
                    {
                      sectionData?.anyOrientedToPerson && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Oriented To Person details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.orientedToPersonDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.orientedToPersonAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Decision Making</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyImpairedDecisionMaking })}>Any Impaired Decision Making:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyImpairedDecisionMaking"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyImpairedDecisionMaking')}
                    </div>

                    {
                      watchAnyImpairedDecisionMaking && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="impairedDecisionMakingDetails" className={classNames({ 'p-error': errors.impairedDecisionMakingDetails })}>
                                Add Impaired Decision Making details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_IMPAIRED_DECISION_MAKING) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="impairedDecisionMakingDetails" control={control}
                                rules={{ required: 'Impaired Decision Making details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Impaired Decision Making details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('impairedDecisionMakingDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="impairedDecisionMakingAssistanceDetails" className={classNames({ 'p-error': errors.impairedDecisionMakingAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="impairedDecisionMakingAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for impaired decision making"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('impairedDecisionMakingAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Impaired Decision Making: </strong>{sectionData?.anyImpairedDecisionMaking ? YES : NO}</div>
                    {
                      sectionData?.anyImpairedDecisionMaking && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Impaired Decision Making details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.impairedDecisionMakingDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.impairedDecisionMakingAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Disruptive Behavior</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyDisruptiveBehavior })}>Any Disruptive Behavior:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyDisruptiveBehavior"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyDisruptiveBehavior')}
                    </div>

                    {
                      watchAnyDisruptiveBehavior && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="disruptiveBehaviorDetails" className={classNames({ 'p-error': errors.disruptiveBehaviorDetails })}>
                                Add Disruptive Behavior details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_DISRUPTIVE_BEHAVIOR) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="disruptiveBehaviorDetails" control={control}
                                rules={{ required: 'Disruptive Behavior details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Disruptive Behavior details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('disruptiveBehaviorDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="disruptiveBehaviorAssistanceDetails" className={classNames({ 'p-error': errors.disruptiveBehaviorAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="disruptiveBehaviorAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for disruptive behavior"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'disruptiveBehaviorAssistanceDetails', SUGGESTED_INSTRUCTIONS_VERBAL_NOISES); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('disruptiveBehaviorAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Disruptive Behavior: </strong>{sectionData?.anyDisruptiveBehavior ? YES : NO}</div>
                    {
                      sectionData?.anyDisruptiveBehavior && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Disruptive Behavior details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.disruptiveBehaviorDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.disruptiveBehaviorAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Assaultive</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyAssaultiveBehavior })}>Any Assaultive:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyAssaultiveBehavior"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyAssaultiveBehavior')}
                    </div>

                    {
                      watchAnyAssaultiveBehavior && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="assaultiveBehaviorDetails" className={classNames({ 'p-error': errors.assaultiveBehaviorDetails })}>
                                Add Assaultive Behavior details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_ASSAULTIVE) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="assaultiveBehaviorDetails" control={control}
                                rules={{ required: 'Assaultive Behavior details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Assaultive Behavior details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('assaultiveBehaviorDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="assaultiveBehaviorAssistanceDetails" className={classNames({ 'p-error': errors.assaultiveBehaviorAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="assaultiveBehaviorAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for assaultive behavior"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'assaultiveBehaviorAssistanceDetails', SUGGESTED_INSTRUCTIONS_ASSAULTIVE_NOT_AT_PERSONAL_CARE); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('assaultiveBehaviorAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Assaultive Behavior: </strong>{sectionData?.anyAssaultiveBehavior ? YES : NO}</div>
                    {
                      sectionData?.anyAssaultiveBehavior && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Assaultive Behavior details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.assaultiveBehaviorDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.assaultiveBehaviorAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Resistive to Care</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyResistanceToCare })}>Any Resistance to Care:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyResistanceToCare"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyResistanceToCare')}
                    </div>

                    {
                      watchAnyResistanceToCare && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="resistanceToCareDetails" className={classNames({ 'p-error': errors.resistanceToCareDetails })}>
                                Add Resistance to Care details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_RESISTIVE) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="resistanceToCareDetails" control={control}
                                rules={{ required: 'Resistance to Care details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Resistance to Care details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('resistanceToCareDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="resistanceToCareAssistanceDetails" className={classNames({ 'p-error': errors.resistanceToCareAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="resistanceToCareAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for resistance to care"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'resistanceToCareAssistanceDetails', SUGGESTED_INSTRUCTIONS_RESISTIVE_TO_CARE_WITH_WORD); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('resistanceToCareAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Resistance to Care: </strong>{sectionData?.anyResistanceToCare ? YES : NO}</div>
                    {
                      sectionData?.anyResistanceToCare && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Resistance to Care details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.resistanceToCareDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.resistanceToCareAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Depression</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyDepression })}>Any Depression:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyDepression"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyDepression')}
                    </div>

                    {
                      watchAnyDepression && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="depressionDetails" className={classNames({ 'p-error': errors.depressionDetails })}>
                                Add Depression details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_DEPRESSION) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="depressionDetails" control={control}
                                rules={{ required: 'Depression details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Depression details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('depressionDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="depressionAssistanceDetails" className={classNames({ 'p-error': errors.depressionAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="depressionAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for depression"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('depressionAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Depression: </strong>{sectionData?.anyDepression ? YES : NO}</div>
                    {
                      sectionData?.anyDepression && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Depression details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.depressionDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.depressionAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Anxiety</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyAnxiety })}>Any Anxiety:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyAnxiety"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyAnxiety')}
                    </div>

                    {
                      watchAnyAnxiety && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="anxietyDetails" className={classNames({ 'p-error': errors.anxietyDetails })}>
                                Add Anxiety details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_ANXIETY) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="anxietyDetails" control={control}
                                rules={{ required: 'Anxiety details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Anxiety details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('anxietyDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="anxietyAssistanceDetails" className={classNames({ 'p-error': errors.anxietyAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="anxietyAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for anxiety"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('anxietyAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Anxiety: </strong>{sectionData?.anyAnxiety ? YES : NO}</div>
                    {
                      sectionData?.anyAnxiety && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Anxiety details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anxietyDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.anxietyAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Irritability</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyIrritability })}>Any Irritability:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyIrritability"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyIrritability')}
                    </div>

                    {
                      watchAnyIrritability && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="irritabilityDetails" className={classNames({ 'p-error': errors.irritabilityDetails })}>
                                Add Irritability details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_IRRITABILITY) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="irritabilityDetails" control={control}
                                rules={{ required: 'Irritability details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Irritability details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('irritabilityDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="irritabilityAssistanceDetails" className={classNames({ 'p-error': errors.irritabilityAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="irritabilityAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for irritability"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'irritabilityAssistanceDetails', SUGGESTED_INSTRUCTIONS_IRRITABLE); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('irritabilityAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Irritability: </strong>{sectionData?.anyIrritability ? YES : NO}</div>
                    {
                      sectionData?.anyIrritability && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Irritability details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.irritabilityDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.irritabilityAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Disorientation</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyDisorientation })}>Any Disorientation:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyDisorientation"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyDisorientation')}
                    </div>

                    {
                      watchAnyDisorientation && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="disorientationDetails" className={classNames({ 'p-error': errors.disorientationDetails })}>
                                Add Disorientation details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_DISORIENTATION) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="disorientationDetails" control={control}
                                rules={{ required: 'Disorientation details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Disorientation details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('disorientationDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="disorientationAssistanceDetails" className={classNames({ 'p-error': errors.disorientationAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="disorientationAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for disorientation"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('disorientationAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Disorientation: </strong>{sectionData?.anyDisorientation ? YES : NO}</div>
                    {
                      sectionData?.anyDisorientation && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Disorientation details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.disorientationDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.disorientationAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Wandering</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyWandering })}>Any Wandering in home/Pacing:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyWandering"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyWandering')}
                    </div>

                    {
                      watchAnyWandering && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="wanderingDetails" className={classNames({ 'p-error': errors.wanderingDetails })}>
                                Add Wandering details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_WANDERING) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="wanderingDetails" control={control}
                                rules={{ required: 'Wandering details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Wandering details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('wanderingDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="wanderingAssistanceDetails" className={classNames({ 'p-error': errors.wanderingAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="wanderingAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for wandering"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'wanderingAssistanceDetails', SUGGESTED_INSTRUCTIONS_WANDERS_NOT_EXIT_SEEKING); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('wanderingAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Wandering: </strong>{sectionData?.anyWandering ? YES : NO}</div>
                    {
                      sectionData?.anyWandering && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Wandering details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.wanderingDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.wanderingAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Exit Seeking</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyExitSeekingBehavior })}>Any Exit Seeking:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyExitSeekingBehavior"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyExitSeekingBehavior')}
                    </div>

                    {
                      watchAnyExitSeekingBehavior && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="exitSeekingBehaviorDetails" className={classNames({ 'p-error': errors.exitSeekingBehaviorDetails })}>
                                Add Exit Seeking details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_EXIT_SEEKING) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="exitSeekingBehaviorDetails" control={control}
                                rules={{ required: 'Exit Seeking details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Exit Seeking details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('exitSeekingBehaviorDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="exitSeekingBehaviorAssistanceDetails" className={classNames({ 'p-error': errors.exitSeekingBehaviorAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="exitSeekingBehaviorAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for exit seeking"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'exitSeekingBehaviorAssistanceDetails', SUGGESTED_INSTRUCTIONS_WANDERS_EXIT_SEEKING); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('exitSeekingBehaviorAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Exit Seeking: </strong>{sectionData?.anyExitSeekingBehavior ? YES : NO}</div>
                    {
                      sectionData?.anyExitSeekingBehavior && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Exit Seeking details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.exitSeekingBehaviorDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.exitSeekingBehaviorAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Hallucinations</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyHallucinations })}>Any Hallucinations:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyHallucinations"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyHallucinations')}
                    </div>

                    {
                      watchAnyHallucinations && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="hallucinationsDetails" className={classNames({ 'p-error': errors.hallucinationsDetails })}>
                                Add Hallucinations details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_HALLUCINATIONS) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="hallucinationsDetails" control={control}
                                rules={{ required: 'Hallucinations details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Hallucinations details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('hallucinationsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="hallucinationsAssistanceDetails" className={classNames({ 'p-error': errors.hallucinationsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="hallucinationsAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for hallucinations"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'hallucinationsAssistanceDetails', SUGGESTED_INSTRUCTIONS_HALLUCINATIONS); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('hallucinationsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Hallucinations: </strong>{sectionData?.anyHallucinations ? YES : NO}</div>
                    {
                      sectionData?.anyHallucinations && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Hallucinations details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.hallucinationsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.hallucinationsAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Delusions</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyDelusions })}>Any Delusions:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyDelusions"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyDelusions')}
                    </div>

                    {
                      watchAnyDelusions && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="delusionsDetails" className={classNames({ 'p-error': errors.delusionsDetails })}>
                                Add Delusions details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_DELUSIONS) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="delusionsDetails" control={control}
                                rules={{ required: 'Delusions details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Delusions details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('delusionsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="delusionsAssistanceDetails" className={classNames({ 'p-error': errors.delusionsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="delusionsAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for delusions"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'delusionsAssistanceDetails', SUGGESTED_INSTRUCTIONS_DELUSIONS); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('delusionsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Delusions: </strong>{sectionData?.anyDelusions ? YES : NO}</div>
                    {
                      sectionData?.anyDelusions && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Delusions details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.delusionsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.delusionsAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Verbal Aggression</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyVerbalAggression })}>Any Verbal Aggression:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyVerbalAggression"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyVerbalAggression')}
                    </div>

                    {
                      watchAnyVerbalAggression && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="verbalAggressionDetails" className={classNames({ 'p-error': errors.verbalAggressionDetails })}>
                                Add Verbal Aggression details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_VERBALLY_AGITATED) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="verbalAggressionDetails" control={control}
                                rules={{ required: 'Verbal Aggression details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Verbal Aggression details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('verbalAggressionDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="verbalAggressionAssistanceDetails" className={classNames({ 'p-error': errors.verbalAggressionAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="verbalAggressionAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for verbal aggression"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'verbalAggressionAssistanceDetails', SUGGESTED_INSTRUCTIONS_VERBAL_NOISES); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('verbalAggressionAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Verbal Aggression: </strong>{sectionData?.anyVerbalAggression ? YES : NO}</div>
                    {
                      sectionData?.anyVerbalAggression && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Verbal Aggression details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.verbalAggressionDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.verbalAggressionAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Physical Aggression</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyPhysicalAggression })}>Any Physical Aggression:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyPhysicalAggression"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyPhysicalAggression')}
                    </div>

                    {
                      watchAnyPhysicalAggression && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="physicalAggressionDetails" className={classNames({ 'p-error': errors.physicalAggressionDetails })}>
                                Add Physical Aggression details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_PHYSICAL_AGITATED) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="physicalAggressionDetails" control={control}
                                rules={{ required: 'Physical Aggression details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Physical Aggression details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('physicalAggressionDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="physicalAggressionAssistanceDetails" className={classNames({ 'p-error': errors.physicalAggressionAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="physicalAggressionAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for physical aggression"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('physicalAggressionAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Physical Aggression: </strong>{sectionData?.anyPhysicalAggression ? YES : NO}</div>
                    {
                      sectionData?.anyPhysicalAggression && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Physical Aggression details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.physicalAggressionDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.physicalAggressionAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Inappropriate Behavior</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyInappropriateBehavior })}>Any Inappropriate or unsafe Behavior:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyInappropriateBehavior"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyInappropriateBehavior')}
                    </div>

                    {
                      watchAnyInappropriateBehavior && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="inappropriateBehaviorDetails" className={classNames({ 'p-error': errors.inappropriateBehaviorDetails })}>
                                Add Inappropriate Behavior details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_INAPPROPRIATE) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="inappropriateBehaviorDetails" control={control}
                                rules={{ required: 'Inappropriate Behavior details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Inappropriate Behavior details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('inappropriateBehaviorDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="inappropriateBehaviorAssistanceDetails" className={classNames({ 'p-error': errors.inappropriateBehaviorAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="inappropriateBehaviorAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for inappropriate behavior"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('inappropriateBehaviorAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Inappropriate Behavior: </strong>{sectionData?.anyInappropriateBehavior ? YES : NO}</div>
                    {
                      sectionData?.anyInappropriateBehavior && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Inappropriate Behavior details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inappropriateBehaviorDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inappropriateBehaviorAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Suicidal</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anySuicidalBehavior })}>Any Suicidal Behavior:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anySuicidalBehavior"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anySuicidalBehavior')}
                    </div>

                    {
                      watchAnySuicidalBehavior && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="suicidalBehaviorDetails" className={classNames({ 'p-error': errors.suicidalBehaviorDetails })}>
                                Add Suicidal Behavior details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_SUICIDAL) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="suicidalBehaviorDetails" control={control}
                                rules={{ required: 'Suicidal Behavior details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Suicidal Behavior details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('suicidalBehaviorDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="suicidalBehaviorAssistanceDetails" className={classNames({ 'p-error': errors.suicidalBehaviorAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="suicidalBehaviorAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for suicidal behavior"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'suicidalBehaviorAssistanceDetails', SUGGESTED_INSTRUCTIONS_SELF_INJURY); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('suicidalBehaviorAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Suicidal Behavior: </strong>{sectionData?.anySuicidalBehavior ? YES : NO}</div>
                    {
                      sectionData?.anySuicidalBehavior && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Suicidal Behavior details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.suicidalBehaviorDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.suicidalBehaviorAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Unfamiliar Situation</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label className={classNames({ 'p-error': errors.anyDifficultyInNewSituations })}>Any Difficulty In New Situations:</label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_UNFAMILIAR_SITUATION) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyDifficultyInNewSituations"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyDifficultyInNewSituations')}
                    </div>

                    {
                      watchAnyDifficultyInNewSituations && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="difficultyInNewSituationsDetails" className={classNames({ 'p-error': errors.difficultyInNewSituationsDetails })}>
                                Add Difficulty In New Situations details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="difficultyInNewSituationsDetails" control={control}
                                rules={{ required: 'Difficulty In New Situations details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Difficulty In New Situations details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('difficultyInNewSituationsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="difficultyInNewSituationsAssistanceDetails" className={classNames({ 'p-error': errors.difficultyInNewSituationsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="difficultyInNewSituationsAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for difficulty in new situations"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('difficultyInNewSituationsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Difficulty In New Situations: </strong>{sectionData?.anyDifficultyInNewSituations ? YES : NO}</div>
                    {
                      sectionData?.anyDifficultyInNewSituations && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Difficulty In New Situations details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.difficultyInNewSituationsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.difficultyInNewSituationsAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Disrobing</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label className={classNames({ 'p-error': errors.anyDisrobing })}>Any Disrobing:</label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_DISROBING) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyDisrobing"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyDisrobing')}
                    </div>

                    {
                      watchAnyDisrobing && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="disrobingDetails" className={classNames({ 'p-error': errors.disrobingDetails })}>
                                Add Disrobing details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="disrobingDetails" control={control}
                                rules={{ required: 'Disrobing details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Disrobing details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('disrobingDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="disrobingAssistanceDetails" className={classNames({ 'p-error': errors.disrobingAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="disrobingAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for disrobing"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('disrobingAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Disrobing: </strong>{sectionData?.anyDisrobing ? YES : NO}</div>
                    {
                      sectionData?.anyDisrobing && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Disrobing details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.disrobingDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.disrobingAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Weeping/Crying</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label className={classNames({ 'p-error': errors.anyWeeping })}>Any Weeping/Crying:</label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_WEEPING) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyWeeping"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyWeeping')}
                    </div>

                    {
                      watchAnyWeeping && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="weepingDetails" className={classNames({ 'p-error': errors.weepingDetails })}>
                                Add Weeping/Crying details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="weepingDetails" control={control}
                                rules={{ required: 'Weeping/Crying details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Weeping/Crying details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('weepingDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="weepingAssistanceDetails" className={classNames({ 'p-error': errors.weepingAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="weepingAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for weeping/crying"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'weepingAssistanceDetails', SUGGESTED_INSTRUCTIONS_CRYTING); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('weepingAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Weeping/Crying: </strong>{sectionData?.anyWeeping ? YES : NO}</div>
                    {
                      sectionData?.anyWeeping && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Weeping/Crying details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.weepingDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.weepingAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Unaware of Consequences</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label className={classNames({ 'p-error': errors.anyUnawareOfConsequences })}>Any Unaware of Consequences:</label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_UNAWARE_OF_CONSEQUENCES) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyUnawareOfConsequences"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyUnawareOfConsequences')}
                    </div>

                    {
                      watchAnyUnawareOfConsequences && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="unawareOfConsequencesDetails" className={classNames({ 'p-error': errors.unawareOfConsequencesDetails })}>
                                Add Unaware of Consequences details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="unawareOfConsequencesDetails" control={control}
                                rules={{ required: 'Unaware of Consequences details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Unaware of Consequences details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('unawareOfConsequencesDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="unawareOfConsequencesAssistanceDetails" className={classNames({ 'p-error': errors.unawareOfConsequencesAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="unawareOfConsequencesAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for unaware of consequences"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('unawareOfConsequencesAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any unaware of consequences: </strong>{sectionData?.anyUnawareOfConsequences ? YES : NO}</div>
                    {
                      sectionData?.anyUnawareOfConsequences && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Unaware of Consequences details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.unawareOfConsequencesDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.unawareOfConsequencesAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Unrealistic fears and suspicions</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label className={classNames({ 'p-error': errors.anyUnrealisticFears })}>Any Unrealistic Fears and Suspicions:</label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_UNREALISTIC_FEARS) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyUnrealisticFears"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyUnrealisticFears')}
                    </div>

                    {
                      watchAnyUnrealisticFears && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="unrealisticFearsDetails" className={classNames({ 'p-error': errors.unrealisticFearsDetails })}>
                                Add Unrealistic Fears and Suspicions details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="unrealisticFearsDetails" control={control}
                                rules={{ required: 'Unrealistic Fears and Suspicions details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Unrealistic Fears and Suspicions details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('unrealisticFearsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="unrealisticFearsAssistanceDetails" className={classNames({ 'p-error': errors.unrealisticFearsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="unrealisticFearsAssistanceDetails" control={control}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for unrealistic fears and suspicions"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'unrealisticFearsAssistanceDetails', SUGGESTED_INSTRUCTIONS_FEAR_SUSPICIOUS); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('unrealisticFearsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Unrealistic Fears and Suspicions: </strong>{sectionData?.anyUnrealisticFears ? YES : NO}</div>
                    {
                      sectionData?.anyUnrealisticFears && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Unrealistic Fears and Suspicions details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.unrealisticFearsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.unrealisticFearsAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Spitting</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label className={classNames({ 'p-error': errors.anyInappropriateSpitting })}>Any Inappropriate Spitting:</label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_SPITTING) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyInappropriateSpitting"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyInappropriateSpitting')}
                    </div>

                    {
                      watchAnyInappropriateSpitting && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="inappropriateSpittingDetails" className={classNames({ 'p-error': errors.inappropriateSpittingDetails })}>
                                Add Inappropriate Spitting details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="inappropriateSpittingDetails" control={control}
                                rules={{ required: 'Inappropriate Spitting details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Inappropriate Spitting details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('inappropriateSpittingDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="inappropriateSpittingAssistanceDetails" className={classNames({ 'p-error': errors.inappropriateSpittingAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="inappropriateSpittingAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for inappropriate spitting"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'inappropriateSpittingAssistanceDetails', SUGGESTED_INSTRUCTIONS_SPITTING); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('inappropriateSpittingAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Inappropriate Spitting: </strong>{sectionData?.anyInappropriateSpitting ? YES : NO}</div>
                    {
                      sectionData?.anyInappropriateSpitting && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Inappropriate Spitting details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inappropriateSpittingDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.inappropriateSpittingAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Breaking/Throwing</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label className={classNames({ 'p-error': errors.anyBreakThings })}>Any Breaking/Throwing:</label>
                        <span className="p-float-label" style={{ cursor: 'pointer' }}
                          onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_BREAKING_THROWING) }}>
                          <i className="pi pi-info-circle"></i>
                        </span>
                      </span>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyBreakThings"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyBreakThings')}
                    </div>

                    {
                      watchAnyBreakThings && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="breakThingsDetails" className={classNames({ 'p-error': errors.breakThingsDetails })}>
                                Add Breaking/Throwing details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="breakThingsDetails" control={control}
                                rules={{ required: 'Breaking/Throwing details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Breaking/Throwing details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('breakThingsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="breakThingsAssistanceDetails" className={classNames({ 'p-error': errors.breakThingsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="breakThingsAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for breaking/throwing"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                              <div className="flex justify-content-center">
                                <Button icon="pi pi-info-circle" label="Suggested instructions" severity="warning" rounded outlined
                                  onClick={(e) => { renderSuggestedInstructionsDialog(e, 'breakThingsAssistanceDetails', SUGGESTED_INSTRUCTIONS_BREAK_THROWS_ITEMS); }} />
                              </div>
                            </span>
                            {getFormErrorMessage('breakThingsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Breaking/Throwing: </strong>{sectionData?.anyBreakingThrowing ? YES : NO}</div>
                    {
                      sectionData?.anyBreakingThrowing && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Breaking/Throwing details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.breakThingsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.breakThingsAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Other Issues</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyOtherPsychSocialCognitiveIssues })}>Any Other Psych Social Cognitive Issues:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyOtherPsychSocialCognitiveIssues"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyOtherPsychSocialCognitiveIssues')}
                    </div>

                    {
                      watchAnyOtherPsychSocialCognitiveIssues && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="otherPsychSocialCognitiveIssuesDetails" className={classNames({ 'p-error': errors.otherPsychSocialCognitiveIssuesDetails })}>
                                Add Other Psych Social Cognitive Issues details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="otherPsychSocialCognitiveIssuesDetails" control={control}
                                rules={{ required: 'Other Psych Social Cognitive Issues details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Other Psych Social Cognitive Issues details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('otherPsychSocialCognitiveIssuesDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="otherPsychSocialCognitiveIssuesAssistanceDetails" className={classNames({ 'p-error': errors.otherPsychSocialCognitiveIssuesAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="otherPsychSocialCognitiveIssuesAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for other psych social cognitive issues"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('otherPsychSocialCognitiveIssuesAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Other Psych Social Cognitive Issues: </strong>{sectionData?.anyOtherPsychSocialCognitiveIssues ? YES : NO}</div>
                    {
                      sectionData?.anyOtherPsychSocialCognitiveIssues && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Other Psych Social Cognitive Issues details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.otherPsychSocialCognitiveIssuesDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.otherPsychSocialCognitiveIssuesAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              {renderRegistryBehaviorSections()}
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Psychopharmacological medications</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyPsychopharmacologicMedications })}>Any Psychopharmacological Medications:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyPsychopharmacologicMedications"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyPsychopharmacologicMedications')}
                    </div>

                    {
                      watchAnyPsychopharmacologicMedications && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="psychopharmacologicMedicationsDetails" className={classNames({ 'p-error': errors.psychopharmacologicMedicationsDetails })}>
                                Add Psychopharmacological Medications details:
                              </label>
                              <span className="p-float-label" style={{ cursor: 'pointer' }}
                                onClick={() => { showHelpMessage && showHelpMessage(RESIDENT_CARE_PLAN_TYPE_MESSAGES.PSYCH_SOCIAL_PSYCHOTIC_DRUGS) }}>
                                <i className="pi pi-info-circle"></i>
                              </span>
                            </span>
                            <span className="p-float-label">
                              <Controller name="psychopharmacologicMedicationsDetails" control={control}
                                rules={{ required: 'Psychopharmacological Medications details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Psychopharmacological Medications details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('psychopharmacologicMedicationsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="psychopharmacologicMedicationsAssistanceDetails" className={classNames({ 'p-error': errors.psychopharmacologicMedicationsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="psychopharmacologicMedicationsAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for psychopharmacological medications"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('psychopharmacologicMedicationsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Psychopharmacological Medications: </strong>{sectionData?.anyPsychopharmacologicMedications ? YES : NO}</div>
                    {
                      sectionData?.anyPsychopharmacologicMedications && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Psychopharmacological Medications details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.psychopharmacologicMedicationsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.psychopharmacologicMedicationsAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Behavioral Health Support Crisis Plan</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyBehavioralHealthSupportCrisisPlan })}>Any Behavioral Health Support Crisis Plan:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyBehavioralHealthSupportCrisisPlan"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyBehavioralHealthSupportCrisisPlan')}
                    </div>

                    {
                      watchAnyBehavioralHealthSupportCrisisPlan && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="behavioralHealthSupportCrisisPlanDetails" className={classNames({ 'p-error': errors.behavioralHealthSupportCrisisPlanDetails })}>
                                Add Behavioral Health Support Crisis Plan details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="behavioralHealthSupportCrisisPlanDetails" control={control}
                                rules={{ required: 'Behavioral Health Support Crisis Plan details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Behavioral Health Support Crisis Plan details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('behavioralHealthSupportCrisisPlanDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="behavioralHealthSupportCrisisPlanAssistanceDetails" className={classNames({ 'p-error': errors.behavioralHealthSupportCrisisPlanAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="behavioralHealthSupportCrisisPlanAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for Behavioral Health Support Crisis Plan"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('behavioralHealthSupportCrisisPlanAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Behavioral Health Support Crisis Plan: </strong>{sectionData?.anyBehavioralHealthSupportCrisisPlan ? YES : NO}</div>
                    {
                      sectionData?.anyBehavioralHealthSupportCrisisPlan && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Behavioral Health Support Crisis Plan details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.behavioralHealthSupportCrisisPlanDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.behavioralHealthSupportCrisisPlanAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Medical Health Provider (MHP)</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyCounselingFromMentalHealthProfessional })}>Any Counseling From Mental Health Professional:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyCounselingFromMentalHealthProfessional"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyCounselingFromMentalHealthProfessional')}
                    </div>

                    {
                      watchAnyCounselingFromMentalHealthProfessional && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="counselingFromMentalHealthProfessionalDetails" className={classNames({ 'p-error': errors.counselingFromMentalHealthProfessionalDetails })}>
                                Add Counseling details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="counselingFromMentalHealthProfessionalDetails" control={control}
                                rules={{ required: 'Counseling details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Counseling details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('counselingFromMentalHealthProfessionalDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="counselingFromMentalHealthProfessionalAssistanceDetails" className={classNames({ 'p-error': errors.counselingFromMentalHealthProfessionalAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="counselingFromMentalHealthProfessionalAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for counseling from mental health professional"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('counselingFromMentalHealthProfessionalAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Counseling from Mental Health Professional: </strong>{sectionData?.anyCounselingFromMentalHealthProfessional ? YES : NO}</div>
                    {
                      sectionData?.anyCounselingFromMentalHealthProfessional && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Counseling details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.counselingFromMentalHealthProfessionalDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.counselingFromMentalHealthProfessionalAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Past behavior - related to Mental Health</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyPastBehaviors })}>Any Past Behaviors:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyPastBehaviors"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyPastBehaviors')}
                    </div>

                    {
                      watchAnyPastBehaviors && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="pastBehaviorsDetails" className={classNames({ 'p-error': errors.pastBehaviorsDetails })}>
                                Add Past Behaviors details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="pastBehaviorsDetails" control={control}
                                rules={{ required: 'Past Behaviors details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Past Behaviors details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('pastBehaviorsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="pastBehaviorsAssistanceDetails" className={classNames({ 'p-error': errors.pastBehaviorsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="pastBehaviorsAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for past behaviors"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('pastBehaviorsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Past Behaviors: </strong>{sectionData?.anyPastBehaviors ? YES : NO}</div>
                    {
                      sectionData?.anyPastBehaviors && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Past Behaviors details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.pastBehaviorsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.pastBehaviorsAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            {/* Meaningful day program UI hidden for both provider and nurse NCP pages per request. */}

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">DSHS - Expanded Community Services (HCS Only)</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyExpandedCommunityServices })}>Any Expanded Community Services:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyExpandedCommunityServices"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyExpandedCommunityServices')}
                    </div>

                    {
                      watchAnyExpandedCommunityServices && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="expandedCommunityServicesDetails" className={classNames({ 'p-error': errors.expandedCommunityServicesDetails })}>
                                Add Expanded Community Services details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="expandedCommunityServicesDetails" control={control}
                                rules={{ required: 'Expanded Community Services details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Expanded Community Services details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('expandedCommunityServicesDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="expandedCommunityServicesAssistanceDetails" className={classNames({ 'p-error': errors.expandedCommunityServicesAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="expandedCommunityServicesAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for expanded community services"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('expandedCommunityServicesAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Expanded Community Services: </strong>{sectionData?.anyExpandedCommunityServices ? YES : NO}</div>
                    {
                      sectionData?.anyExpandedCommunityServices && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Expanded Community Services details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.expandedCommunityServicesDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.expandedCommunityServicesAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">DSHS - Specialized Behavior Services (HCS Only)</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anySpecializedBehavioralPrograms })}>Any Specialized Behavioral Programs:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anySpecializedBehavioralPrograms"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anySpecializedBehavioralPrograms')}
                    </div>

                    {
                      watchAnySpecializedBehavioralPrograms && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="specializedBehavioralProgramsDetails" className={classNames({ 'p-error': errors.specializedBehavioralProgramsDetails })}>
                                Add Specialized Behavioral Programs details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="specializedBehavioralProgramsDetails" control={control}
                                rules={{ required: 'Specialized Behavioral Programs details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Specialized Behavioral Programs details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('specializedBehavioralProgramsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="specializedBehavioralProgramsAssistanceDetails" className={classNames({ 'p-error': errors.specializedBehavioralProgramsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="specializedBehavioralProgramsAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for specialized behavioral programs"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('specializedBehavioralProgramsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Specialized Behavioral Programs: </strong>{sectionData?.anySpecializedBehavioralPrograms ? YES : NO}</div>
                    {
                      sectionData?.anySpecializedBehavioralPrograms && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Specialized Behavioral Programs details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.specializedBehavioralProgramsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.specializedBehavioralProgramsAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">DSHS - Mental Health Program</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyMentalHealthProgram })}>Any Mental Health Program:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyMentalHealthProgram"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyMentalHealthProgram')}
                    </div>

                    {
                      watchAnyMentalHealthProgram && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="mentalHealthProgramDetails" className={classNames({ 'p-error': errors.mentalHealthProgramDetails })}>
                                Add Mental Health Program details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="mentalHealthProgramDetails" control={control}
                                rules={{ required: 'Mental Health Program details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Mental Health Program details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('mentalHealthProgramDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="mentalHealthProgramAssistanceDetails" className={classNames({ 'p-error': errors.mentalHealthProgramAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="mentalHealthProgramAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for mental health program"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('mentalHealthProgramAssistanceDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="mentalHealthProgramContactInfo" className={classNames({ 'p-error': errors.mentalHealthProgramContactInfo })}>
                                Add any required contact information:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="mentalHealthProgramContactInfo" control={control}
                                rules={{ required: 'Contact information is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any contact information required for mental health program"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('mentalHealthProgramContactInfo')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Mental Health Program: </strong>{sectionData?.anyMentalHealthProgram ? YES : NO}</div>
                    {
                      sectionData?.anyMentalHealthProgram && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Mental Health Program details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.mentalHealthProgramDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.mentalHealthProgramAssistanceDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Contact Information: </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.mentalHealthProgramContactInfo || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            {/* Meaningful day intervention UI hidden for both provider and nurse NCP pages per request. */}

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Intervention - Other</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <label className={classNames({ 'p-error': errors.anyOtherInterventions })}>Any Other Interventions:</label>
                      <div className="inline-flex m-2 span-color-white">
                        <Controller
                          name="anyOtherInterventions"
                          control={control}
                          render={({ field }) => (
                            <ToggleButton onLabel="Yes" offLabel="No"
                              {...field}
                              checked={field.value}
                              onChange={(e) => field.onChange(e.value)} />
                          )}
                        />
                      </div>
                      {getFormErrorMessage('anyOtherInterventions')}
                    </div>

                    {
                      watchAnyOtherInterventions && (
                        <>
                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="otherInterventionsDetails" className={classNames({ 'p-error': errors.otherInterventionsDetails })}>
                                Add Other Interventions details:
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="otherInterventionsDetails" control={control}
                                rules={{ required: 'Other Interventions details is required.' }}
                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Add Other Interventions details"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('otherInterventionsDetails')}
                          </div>

                          <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                            <span className="flex align-items-center gap-2 mb-2">
                              <label htmlFor="otherInterventionsAssistanceDetails" className={classNames({ 'p-error': errors.otherInterventionsAssistanceDetails })}>
                                {ASSISTANCE_REQUIRED_MESSAGE}
                              </label>
                            </span>
                            <span className="p-float-label">
                              <Controller name="otherInterventionsAssistanceDetails" control={control}

                                render={({ field, fieldState }) => (
                                  <InputTextarea placeholder="Describe any assistance required for other interventions"
                                    style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                                )} />
                            </span>
                            {getFormErrorMessage('otherInterventionsAssistanceDetails')}
                          </div>
                        </>
                      )}
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}><strong>Any Other Interventions: </strong>{sectionData?.anyOtherInterventions ? YES : NO}</div>
                    {
                      sectionData?.anyOtherInterventions && (
                        <>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Other Interventions details </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.otherInterventionsDetails || NA)}</span>
                          </div>
                          <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>{ASSISTANCE_REQUIRED_MESSAGE} </strong>
                            <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.otherInterventionsAssistanceDetails || NA)}</span>
                          </div>
                        </>
                      )
                    }
                  </div>
                )
              }
            </div>

            <div className="flex-column flex-wrap ">
              <Divider><Tag severity="warning">Typical day routine</Tag></Divider>
              {
                enableEditing ? (
                  <div className="flex flex-wrap">
                    <div className="col flex-column flex-wrap mt-2" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)" }}>
                      <span className="flex align-items-center gap-2 mb-2">
                        <label htmlFor="normalDayRoutineDetails" className={classNames({ 'p-error': errors.normalDayRoutineDetails })}>
                          Add typical day routine details:
                        </label>
                      </span>
                      <span className="p-float-label">
                        <Controller name="normalDayRoutineDetails" control={control}
                          render={({ field, fieldState }) => (
                            <InputTextarea placeholder="(optional) - What does a typical day look like? Include meal times, activities, therapies, rest periods, etc."
                              style={{ width: '100%' }} rows={5} id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                          )} />
                      </span>
                      {getFormErrorMessage('normalDayRoutineDetails')}
                    </div>
                  </div>) : (
                  <div className="flex flex-wrap justify-content-center align-items-center">
                    <div className="col flex flex-column" style={{ minWidth: "250px", flex: "1 1 calc(33% - 1em)", whiteSpace: "pre-wrap" }}><strong>Typical Day Routine details </strong>
                      <span style={{ whiteSpace: "pre-wrap", border: "1px dashed lightgray", padding: "0.5em" }}>{(sectionData?.normalDayRoutineDetails || NA)}</span>
                    </div>
                  </div>
                )
              }
            </div>
          </form>
        </div>
      </Card >
    </div >
  );
};

export default PsychSocialSection;


